import {
  CssVariableMap,
  OrgBrandBase,
  ResolvedOrgTheme,
  ThemeMode,
  ThemePair,
  ThemeTokens,
} from "./themeTypes";

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const LEGACY_THEME_KEYS = [
  "primary",
  "accent",
  "background",
  "bg",
  "surface",
  "surfaceAlt",
  "text",
  "muted",
  "border",
  "chart1",
  "chart2",
  "chart3",
  "chart4",
  "chart5",
];

export const DEFAULT_ORG_ID = "org_default";
const NIA_BRANDED_ORG_IDS = new Set([DEFAULT_ORG_ID, "org_shuttle"]);

export const DEFAULT_BRAND_BASE: OrgBrandBase = Object.freeze({
  primary: "#0033CC",
  accent: "#18DEB8",
});

export const NIA_BRAND = Object.freeze({
  id: DEFAULT_ORG_ID,
  name: "NIA",
  primary: "#0033CC",
  accent: "#18DEB8",
  white: "#FFFFFF",
});

export const DEFAULT_BRAND_COLORS = DEFAULT_BRAND_BASE;
export const NIA_BRAND_COLORS = Object.freeze({
  primary: NIA_BRAND.primary,
  accent: NIA_BRAND.accent,
});

const FONT_TOKENS = Object.freeze({
  sans: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
  display: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
  mono: '"SFMono-Regular", "SF Mono", Consolas, "Liberation Mono", Menlo, monospace',
});

const TEXT_SIZE_TOKENS = Object.freeze({
  xs: "0.75rem",
  sm: "0.875rem",
  md: "1rem",
  lg: "1.125rem",
  xl: "1.25rem",
  "2xl": "1.5rem",
});

const FONT_WEIGHT_TOKENS = Object.freeze({
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
});

const LINE_HEIGHT_TOKENS = Object.freeze({
  tight: 1.2,
  normal: 1.5,
  relaxed: 1.7,
});

const RADIUS_TOKENS = Object.freeze({
  sm: "6px",
  md: "10px",
  lg: "14px",
  xl: "18px",
  "2xl": "24px",
});

const SPACE_TOKENS = Object.freeze({
  1: "4px",
  2: "8px",
  3: "12px",
  4: "16px",
  5: "20px",
  6: "24px",
  7: "32px",
  8: "40px",
  9: "48px",
  10: "64px",
});

const BORDER_WIDTH_TOKENS = Object.freeze({
  hairline: "1px",
  normal: "1px",
  strong: "2px",
});

type CoreSeed = {
  primary: string;
  accent: string;
  background: string;
  surface: string;
  surfaceAlt: string;
  text: string;
  textMuted: string;
  border: string;
  borderStrong: string;
  focusRing: string;
  shadowColor: string;
  success: string;
  warning: string;
  danger: string;
  info: string;
  chart1: string;
  chart2: string;
  chart3: string;
  chart4: string;
  chart5: string;
  chart6: string;
};

export function clamp(value: number, min = 0, max = 1): number {
  return Math.min(max, Math.max(min, value));
}

export function normalizeHex(value: string | undefined | null, fallback = "#000000"): string {
  const input = String(value || "").trim();
  if (!HEX_RE.test(input)) return fallback.toLowerCase();
  if (input.length === 4) {
    return `#${input[1]}${input[1]}${input[2]}${input[2]}${input[3]}${input[3]}`.toLowerCase();
  }
  return input.toLowerCase();
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const safe = normalizeHex(hex);
  return {
    r: parseInt(safe.slice(1, 3), 16),
    g: parseInt(safe.slice(3, 5), 16),
    b: parseInt(safe.slice(5, 7), 16),
  };
}

export function rgbToHex(rgb: { r: number; g: number; b: number }): string {
  const toHex = (value: number) => Math.round(clamp(value / 255, 0, 1) * 255).toString(16).padStart(2, "0");
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}

export function mix(hexA: string, hexB: string, ratio = 0.5): string {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  const t = clamp(ratio);
  return rgbToHex({
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  });
}

export function lighten(hex: string, ratio = 0.1): string {
  return mix(hex, "#ffffff", ratio);
}

export function darken(hex: string, ratio = 0.1): string {
  return mix(hex, "#000000", ratio);
}

export function alpha(hex: string, opacity = 1): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${clamp(opacity)})`;
}

export function luminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  const convert = (channel: number) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * convert(r) + 0.7152 * convert(g) + 0.0722 * convert(b);
}

export function getContrastRatio(foreground: string, background: string): number {
  const l1 = luminance(foreground);
  const l2 = luminance(background);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

export function pickReadableText(background: string, dark = "#0f172a", light = "#f8fafc"): string {
  return getContrastRatio(dark, background) >= getContrastRatio(light, background) ? dark : light;
}

export function ensureContrast(foreground: string, background: string, minimum = 4.5): string {
  let next = normalizeHex(foreground);
  const bg = normalizeHex(background);
  let attempts = 0;
  const prefersDarkening = luminance(next) > luminance(bg);
  while (getContrastRatio(next, bg) < minimum && attempts < 18) {
    next = prefersDarkening ? darken(next, 0.08) : lighten(next, 0.08);
    attempts += 1;
  }
  return next;
}

function createShadowTokens(shadowColor: string) {
  return {
    sm: `0 4px 12px ${alpha(shadowColor, 0.08)}`,
    md: `0 12px 30px ${alpha(shadowColor, 0.14)}`,
    lg: `0 18px 40px ${alpha(shadowColor, 0.2)}`,
  };
}

function buildThemeFromCore(core: CoreSeed, mode: ThemeMode): ThemeTokens {
  const onPrimary = pickReadableText(core.primary);
  const secondaryBg = mode === "dark" ? mix(core.surfaceAlt, core.background, 0.35) : mix(core.surfaceAlt, core.background, 0.15);
  const secondaryHover = mode === "dark" ? lighten(secondaryBg, 0.06) : darken(secondaryBg, 0.04);
  const ghostHover = mode === "dark" ? alpha(core.text, 0.08) : alpha(core.text, 0.05);
  const inputBg = mode === "dark" ? mix(core.surface, core.background, 0.15) : "#ffffff";
  const userBubbleBg = mode === "dark" ? mix(core.primary, core.background, 0.35) : mix(core.primary, core.background, 0.82);
  const aiBubbleBg = mode === "dark" ? mix(core.surfaceAlt, core.background, 0.25) : mix(core.surfaceAlt, core.background, 0.1);
  const shadow = createShadowTokens(core.shadowColor);

  return {
    mode,
    color: {
      primary: core.primary,
      accent: core.accent,
      background: core.background,
      surface: core.surface,
      surfaceAlt: core.surfaceAlt,
      text: core.text,
      textMuted: core.textMuted,
      border: core.border,
      borderStrong: core.borderStrong,
      focusRing: core.focusRing,
      shadowColor: core.shadowColor,
      success: core.success,
      warning: core.warning,
      danger: core.danger,
      info: core.info,
    },
    button: {
      primary: {
        bg: core.primary,
        fg: onPrimary,
        hoverBg: mode === "dark" ? lighten(core.primary, 0.08) : darken(core.primary, 0.1),
        activeBg: mode === "dark" ? lighten(core.primary, 0.14) : darken(core.primary, 0.16),
      },
      secondary: {
        bg: secondaryBg,
        fg: core.text,
        hoverBg: secondaryHover,
        activeBg: mode === "dark" ? lighten(secondaryHover, 0.06) : darken(secondaryHover, 0.06),
        border: core.border,
      },
      ghost: {
        fg: core.text,
        hoverBg: ghostHover,
      },
    },
    input: {
      bg: inputBg,
      fg: core.text,
      placeholder: core.textMuted,
      border: core.border,
      focusBorder: core.focusRing,
    },
    card: {
      bg: core.surface,
      border: core.border,
      shadow: shadow.md,
    },
    sidebar: {
      bg: core.surface,
      fg: core.text,
      muted: core.textMuted,
      border: core.border,
      activeBg: core.primary,
      activeFg: onPrimary,
      hoverBg: mode === "dark" ? alpha(core.text, 0.06) : alpha(core.text, 0.04),
    },
    badge: {
      bg: mode === "dark" ? alpha(core.accent, 0.22) : alpha(core.accent, 0.14),
      fg: core.text,
      border: mode === "dark" ? alpha(core.accent, 0.4) : alpha(core.accent, 0.24),
    },
    chat: {
      userBubbleBg,
      userBubbleFg: pickReadableText(userBubbleBg),
      aiBubbleBg,
      aiBubbleFg: core.text,
      bubbleBorder: core.border,
    },
    font: { ...FONT_TOKENS },
    textSize: { ...TEXT_SIZE_TOKENS },
    fontWeight: { ...FONT_WEIGHT_TOKENS },
    lineHeight: { ...LINE_HEIGHT_TOKENS },
    radius: { ...RADIUS_TOKENS },
    space: { ...SPACE_TOKENS },
    shadow,
    borderWidth: { ...BORDER_WIDTH_TOKENS },
    chart: {
      1: core.chart1,
      2: core.chart2,
      3: core.chart3,
      4: core.chart4,
      5: core.chart5,
      6: core.chart6,
    },
  };
}

export function createNiaThemePair(): ThemePair {
  return {
    light: buildThemeFromCore(
      {
        primary: "#0033CC",
        accent: "#18DEB8",
        background: "#F7FBFF",
        surface: "#ffffff",
        surfaceAlt: "#EAF7F4",
        text: "#0F172A",
        textMuted: "#7A8699",
        border: "#D6E2F0",
        borderStrong: "#B7C9E0",
        focusRing: "#6E8FFF",
        shadowColor: "#0f172a",
        success: "#0f766e",
        warning: "#b45309",
        danger: "#b91c1c",
        info: "#1d4ed8",
        chart1: "#0033CC",
        chart2: "#18DEB8",
        chart3: "#023C40",
        chart4: "#5518DE",
        chart5: "#DE183E",
        chart6: "#D0FFE8",
      },
      "light"
    ),
    dark: buildThemeFromCore(
      {
        primary: "#7A85FF",
        accent: "#48B6BC",
        background: "#0B0D1A",
        surface: "#12152A",
        surfaceAlt: "#1B2040",
        text: "#F1F5F9",
        textMuted: "#8A8D95",
        border: "#2B315A",
        borderStrong: "#465181",
        focusRing: "#649BE1",
        shadowColor: "#000000",
        success: "#2dd4bf",
        warning: "#f59e0b",
        danger: "#f87171",
        info: "#60a5fa",
        chart1: "#4D5CFF",
        chart2: "#20A6AD",
        chart3: "#2C8F95",
        chart4: "#8892FF",
        chart5: "#38D0D7",
        chart6: "#60A5FA",
      },
      "dark"
    ),
  };
}

export const NIA_THEME = createNiaThemePair();

function generateCoreSeed(base: OrgBrandBase, mode: ThemeMode): CoreSeed {
  const primary = normalizeHex(base.primary, DEFAULT_BRAND_BASE.primary);
  const accent = normalizeHex(base.accent, DEFAULT_BRAND_BASE.accent);
  const background = mode === "dark"
    ? normalizeHex(base.background, mix(primary, "#050816", 0.9))
    : normalizeHex(base.background, mix(primary, "#ffffff", 0.97));
  const surface = mode === "dark"
    ? normalizeHex(base.surface, mix(primary, "#0f172a", 0.78))
    : normalizeHex(base.surface, "#ffffff");
  const surfaceAlt = mode === "dark"
    ? normalizeHex(base.surfaceAlt, mix(accent, "#111827", 0.68))
    : normalizeHex(base.surfaceAlt, mix(accent, "#ffffff", 0.92));
  const text = normalizeHex(base.text, ensureContrast(pickReadableText(background), background, 7));
  const textMuted = mix(text, background, mode === "dark" ? 0.45 : 0.55);
  const border = normalizeHex(base.border, mix(text, background, mode === "dark" ? 0.78 : 0.82));
  const borderStrong = mix(text, background, mode === "dark" ? 0.62 : 0.68);
  const shadowColor = mode === "dark" ? "#000000" : "#0f172a";
  const themedPrimary = mode === "dark" ? ensureContrast(lighten(primary, 0.25), background, 3.2) : primary;
  const themedAccent = mode === "dark" ? ensureContrast(lighten(accent, 0.18), background, 3.2) : accent;

  return {
    primary: themedPrimary,
    accent: themedAccent,
    background,
    surface,
    surfaceAlt,
    text,
    textMuted,
    border,
    borderStrong,
    focusRing: mix(themedPrimary, themedAccent, 0.45),
    shadowColor,
    success: mode === "dark" ? "#34d399" : "#15803d",
    warning: mode === "dark" ? "#fbbf24" : "#b45309",
    danger: mode === "dark" ? "#f87171" : "#b91c1c",
    info: mode === "dark" ? "#60a5fa" : "#1d4ed8",
    chart1: themedPrimary,
    chart2: themedAccent,
    chart3: mix(themedPrimary, themedAccent, 0.5),
    chart4: mode === "dark" ? lighten(themedPrimary, 0.24) : lighten(themedPrimary, 0.32),
    chart5: mode === "dark" ? lighten(themedAccent, 0.24) : lighten(themedAccent, 0.28),
    chart6: mode === "dark" ? mix(themedAccent, "#93c5fd", 0.4) : mix(themedPrimary, "#38bdf8", 0.4),
  };
}

export function createOrgTheme(base: OrgBrandBase, mode: ThemeMode): ThemeTokens {
  if (
    NIA_BRANDED_ORG_IDS.has(String(base.id || "")) &&
    normalizeHex(base.primary, NIA_BRAND.primary) === NIA_BRAND.primary.toLowerCase() &&
    normalizeHex(base.accent, NIA_BRAND.accent) === NIA_BRAND.accent.toLowerCase()
  ) {
    return createNiaThemePair()[mode];
  }

  return buildThemeFromCore(generateCoreSeed(base, mode), mode);
}

export function createOrgThemePair(base: OrgBrandBase): ThemePair {
  if (
    NIA_BRANDED_ORG_IDS.has(String(base.id || "")) &&
    normalizeHex(base.primary, NIA_BRAND.primary) === NIA_BRAND.primary.toLowerCase() &&
    normalizeHex(base.accent, NIA_BRAND.accent) === NIA_BRAND.accent.toLowerCase()
  ) {
    return createNiaThemePair();
  }

  return {
    light: createOrgTheme(base, "light"),
    dark: createOrgTheme(base, "dark"),
  };
}

export function generateThemeFromBrandColors(primaryHex: string, accentHex: string): ThemePair {
  return createOrgThemePair({ primary: primaryHex, accent: accentHex });
}

function isThemeTokens(input: any): input is ThemeTokens {
  return Boolean(input && typeof input === "object" && input.color && input.button && input.input && input.card);
}

function isDarkThemeInputWeak(input: any, resolvedDark: ThemeTokens): boolean {
  if (!input || typeof input !== "object") return false;
  const bgLum = luminance(resolvedDark.color.background);
  const surfaceLum = luminance(resolvedDark.color.surface);
  const textContrast = getContrastRatio(resolvedDark.color.text, resolvedDark.color.background);
  const borderContrast = getContrastRatio(resolvedDark.color.border, resolvedDark.color.background);

  // Reject "dark" palettes that are effectively light/washed.
  if (bgLum > 0.12) return true;
  if (surfaceLum > 0.2) return true;
  if (textContrast < 4.5) return true;
  if (borderContrast < 1.2) return true;
  return false;
}

export function hasExplicitTheme(theme: any): boolean {
  return Boolean(theme && typeof theme === "object" && theme.light && theme.dark);
}

export function hasPartialThemePair(theme: any): boolean {
  return Boolean(theme && typeof theme === "object" && (theme.light || theme.dark));
}

export function hasLegacyFlatTheme(theme: any): boolean {
  return Boolean(theme && typeof theme === "object" && !theme.light && !theme.dark && LEGACY_THEME_KEYS.some((key) => theme[key]));
}

function overlayThemeTokens(baseTheme: ThemeTokens, overrides: any, mode: ThemeMode): ThemeTokens {
  const patch = overrides || {};
  const primary = normalizeHex(patch.primary || patch.color?.primary, baseTheme.color.primary);
  const accent = normalizeHex(patch.accent || patch.color?.accent, baseTheme.color.accent);
  const background = normalizeHex(patch.background || patch.bg || patch.color?.background, baseTheme.color.background);
  const text = normalizeHex(patch.text || patch.color?.text, baseTheme.color.text);
  const border = normalizeHex(patch.border || patch.color?.border, baseTheme.color.border);
  const derived = createOrgTheme(
    {
      primary,
      accent,
      background,
      text,
      border,
      surface: patch.surface || patch.color?.surface || baseTheme.color.surface,
      surfaceAlt: patch.surfaceAlt || patch.color?.surfaceAlt || baseTheme.color.surfaceAlt,
    },
    mode
  );

  return {
    ...derived,
    color: {
      ...derived.color,
      success: normalizeHex(patch.success || patch.color?.success, derived.color.success),
      warning: normalizeHex(patch.warning || patch.color?.warning, derived.color.warning),
      danger: normalizeHex(patch.danger || patch.color?.danger, derived.color.danger),
      info: normalizeHex(patch.info || patch.color?.info, derived.color.info),
    },
    chart: {
      ...derived.chart,
      1: normalizeHex(patch.chart1 || patch.chart?.["1"], derived.chart[1]),
      2: normalizeHex(patch.chart2 || patch.chart?.["2"], derived.chart[2]),
      3: normalizeHex(patch.chart3 || patch.chart?.["3"], derived.chart[3]),
      4: normalizeHex(patch.chart4 || patch.chart?.["4"], derived.chart[4]),
      5: normalizeHex(patch.chart5 || patch.chart?.["5"], derived.chart[5]),
      6: normalizeHex(patch.chart6 || patch.chart?.["6"], derived.chart[6]),
    },
  };
}

export function normalizeThemeTokens(input: any = {}, mode: ThemeMode = "light", fallbackTokens?: ThemeTokens): ThemeTokens {
  const fallback = fallbackTokens || createOrgTheme(DEFAULT_BRAND_BASE, mode);
  if (isThemeTokens(input)) {
    return overlayThemeTokens(fallback, input, mode);
  }
  return overlayThemeTokens(fallback, input, mode);
}

export function normalizeThemePair(theme: any, brandBase: OrgBrandBase = DEFAULT_BRAND_BASE): ThemePair {
  const resolvedBase: OrgBrandBase = {
    id: brandBase.id,
    primary: normalizeHex(brandBase.primary, DEFAULT_BRAND_BASE.primary),
    accent: normalizeHex(brandBase.accent, DEFAULT_BRAND_BASE.accent),
    background: brandBase.background,
    text: brandBase.text,
    border: brandBase.border,
    surface: brandBase.surface,
    surfaceAlt: brandBase.surfaceAlt,
  };
  const generated = createOrgThemePair(resolvedBase);

  if (hasPartialThemePair(theme)) {
    const light = normalizeThemeTokens(theme.light || generated.light, "light", generated.light);
    const candidateDark = normalizeThemeTokens(theme.dark || generated.dark, "dark", generated.dark);
    const dark = isDarkThemeInputWeak(theme.dark, candidateDark) ? generated.dark : candidateDark;
    return {
      light,
      dark,
    };
  }

  if (hasLegacyFlatTheme(theme)) {
    const {
      background: _bg1,
      bg: _bg2,
      surface: _surface,
      surfaceAlt: _surfaceAlt,
      text: _text,
      muted: _muted,
      border: _border,
      ...legacyDarkPatch
    } = theme || {};
    const light = normalizeThemeTokens(theme, "light", generated.light);
    const candidateDark = normalizeThemeTokens(
      {
        ...legacyDarkPatch,
        primary: theme.primary || generated.dark.color.primary,
        accent: theme.accent || generated.dark.color.accent,
      },
      "dark",
      generated.dark
    );
    const dark = isDarkThemeInputWeak(theme, candidateDark) ? generated.dark : candidateDark;
    return {
      light,
      dark,
    };
  }

  return generated;
}

export function resolveOrgTheme(org: any = {}): ResolvedOrgTheme {
  if (NIA_BRANDED_ORG_IDS.has(String(org.id || ""))) {
    const brandColors: OrgBrandBase = {
      id: org.id || DEFAULT_ORG_ID,
      primary: NIA_BRAND.primary,
      accent: NIA_BRAND.accent,
    };
    return {
      brandColors,
      theme: createNiaThemePair(),
      isGenerated: false,
      hasCustomTheme: true,
    };
  }

  const brandColors: OrgBrandBase = {
    id: org.id,
    primary: normalizeHex(org.primary || org.brandColors?.primary || org.theme?.light?.color?.primary || org.theme?.primary, DEFAULT_BRAND_BASE.primary),
    accent: normalizeHex(org.accent || org.brandColors?.accent || org.theme?.light?.color?.accent || org.theme?.accent, DEFAULT_BRAND_BASE.accent),
  };
  const hasCustomTheme = hasPartialThemePair(org.theme) || hasLegacyFlatTheme(org.theme);

  return {
    brandColors,
    theme: hasCustomTheme ? normalizeThemePair(org.theme, brandColors) : createOrgThemePair(brandColors),
    isGenerated: !hasCustomTheme,
    hasCustomTheme,
  };
}

export function getDefaultOrgTheme(): ThemePair {
  return createOrgThemePair(DEFAULT_BRAND_BASE);
}

export function getThemeModeTokens(theme: ThemePair | any, mode: ThemeMode = "light"): ThemeTokens {
  const safeMode = mode === "dark" ? "dark" : "light";
  const resolved = normalizeThemePair(theme, DEFAULT_BRAND_BASE);
  return resolved[safeMode];
}

export function getThemeCssVariables(tokens: ThemeTokens): CssVariableMap {
  const theme = isThemeTokens(tokens) ? tokens : normalizeThemeTokens(tokens, "light");
  const uiAccent = theme.mode === "dark" ? theme.chart[2] : theme.chart[1];
  const uiAccent2 = theme.mode === "dark" ? theme.chart[3] : theme.chart[2];
  const uiAccent3 = theme.mode === "dark" ? theme.chart[4] : theme.chart[3];
  const uiAccent4 = theme.mode === "dark" ? theme.chart[5] : theme.chart[4];
  const uiAccent5 = theme.mode === "dark" ? theme.chart[6] : theme.chart[5];
  const gradient = theme.mode === "dark"
    ? `radial-gradient(130% 90% at 12% 8%, ${alpha(theme.color.primary, 0.22)} 0%, ${alpha(theme.color.accent, 0.1)} 38%, transparent 72%), linear-gradient(140deg, ${theme.color.background} 0%, ${mix(theme.color.background, theme.color.surfaceAlt, 0.28)} 100%)`
    : `radial-gradient(120% 86% at 8% 2%, ${alpha(theme.color.primary, 0.08)} 0%, ${alpha(theme.color.accent, 0.06)} 42%, transparent 74%), linear-gradient(145deg, ${theme.color.background} 0%, ${mix(theme.color.surfaceAlt, theme.color.background, 0.65)} 100%)`;
  const glowAccent = alpha(theme.color.accent, theme.mode === "dark" ? 0.2 : 0.14);
  const cardShadowHover = theme.mode === "dark"
    ? `0 18px 38px ${alpha(theme.color.shadowColor, 0.32)}, 0 0 0 1px ${alpha(theme.color.accent, 0.18)}`
    : `0 16px 34px ${alpha(theme.color.shadowColor, 0.18)}, 0 0 0 1px ${alpha(theme.color.accent, 0.14)}`;
  return {
    "--color-primary": theme.color.primary,
    "--color-accent": theme.color.accent,
    "--color-background": theme.color.background,
    "--color-surface": theme.color.surface,
    "--color-surface-alt": theme.color.surfaceAlt,
    "--color-text": theme.color.text,
    "--color-text-muted": theme.color.textMuted,
    "--color-border": theme.color.border,
    "--color-border-strong": theme.color.borderStrong,
    "--color-focus-ring": theme.color.focusRing,
    "--color-shadow-color": theme.color.shadowColor,
    "--color-success": theme.color.success,
    "--color-warning": theme.color.warning,
    "--color-danger": theme.color.danger,
    "--color-info": theme.color.info,
    "--button-primary-bg": theme.button.primary.bg,
    "--button-primary-fg": theme.button.primary.fg,
    "--button-primary-hover-bg": theme.button.primary.hoverBg,
    "--button-primary-active-bg": theme.button.primary.activeBg,
    "--button-secondary-bg": theme.button.secondary.bg,
    "--button-secondary-fg": theme.button.secondary.fg,
    "--button-secondary-hover-bg": theme.button.secondary.hoverBg,
    "--button-secondary-active-bg": theme.button.secondary.activeBg,
    "--button-secondary-border": theme.button.secondary.border || theme.color.border,
    "--button-ghost-fg": theme.button.ghost.fg,
    "--button-ghost-hover-bg": theme.button.ghost.hoverBg,
    "--input-bg": theme.input.bg,
    "--input-fg": theme.input.fg,
    "--input-placeholder": theme.input.placeholder,
    "--input-border": theme.input.border,
    "--input-focus-border": theme.input.focusBorder,
    "--card-bg": theme.card.bg,
    "--card-border": theme.card.border,
    "--card-shadow": theme.card.shadow,
    "--card-shadow-hover": cardShadowHover,
    "--sidebar-bg": theme.sidebar.bg,
    "--sidebar-fg": theme.sidebar.fg,
    "--sidebar-muted": theme.sidebar.muted,
    "--sidebar-border": theme.sidebar.border,
    "--sidebar-active-bg": theme.sidebar.activeBg,
    "--sidebar-active-fg": theme.sidebar.activeFg,
    "--sidebar-hover-bg": theme.sidebar.hoverBg,
    "--badge-bg": theme.badge.bg,
    "--badge-fg": theme.badge.fg,
    "--badge-border": theme.badge.border,
    "--chat-user-bubble-bg": theme.chat.userBubbleBg,
    "--chat-user-bubble-fg": theme.chat.userBubbleFg,
    "--chat-ai-bubble-bg": theme.chat.aiBubbleBg,
    "--chat-ai-bubble-fg": theme.chat.aiBubbleFg,
    "--chat-bubble-border": theme.chat.bubbleBorder,
    "--font-sans": theme.font.sans,
    "--font-display": theme.font.display,
    "--font-mono": theme.font.mono,
    "--text-size-xs": theme.textSize.xs,
    "--text-size-sm": theme.textSize.sm,
    "--text-size-md": theme.textSize.md,
    "--text-size-lg": theme.textSize.lg,
    "--text-size-xl": theme.textSize.xl,
    "--text-size-2xl": theme.textSize["2xl"],
    "--font-weight-regular": String(theme.fontWeight.regular),
    "--font-weight-medium": String(theme.fontWeight.medium),
    "--font-weight-semibold": String(theme.fontWeight.semibold),
    "--font-weight-bold": String(theme.fontWeight.bold),
    "--line-height-tight": String(theme.lineHeight.tight),
    "--line-height-normal": String(theme.lineHeight.normal),
    "--line-height-relaxed": String(theme.lineHeight.relaxed),
    "--radius-sm": theme.radius.sm,
    "--radius-md": theme.radius.md,
    "--radius-lg": theme.radius.lg,
    "--radius-xl": theme.radius.xl,
    "--radius-2xl": theme.radius["2xl"],
    "--space-1": theme.space[1],
    "--space-2": theme.space[2],
    "--space-3": theme.space[3],
    "--space-4": theme.space[4],
    "--space-5": theme.space[5],
    "--space-6": theme.space[6],
    "--space-7": theme.space[7],
    "--space-8": theme.space[8],
    "--space-9": theme.space[9],
    "--space-10": theme.space[10],
    "--shadow-sm": theme.shadow.sm,
    "--shadow-md": theme.shadow.md,
    "--shadow-lg": theme.shadow.lg,
    "--border-width-hairline": theme.borderWidth.hairline,
    "--border-width-normal": theme.borderWidth.normal,
    "--border-width-strong": theme.borderWidth.strong,
    "--chart-1": theme.chart[1],
    "--chart-2": theme.chart[2],
    "--chart-3": theme.chart[3],
    "--chart-4": theme.chart[4],
    "--chart-5": theme.chart[5],
    "--chart-6": theme.chart[6],
    "--brand": theme.color.primary,
    "--bg": theme.color.background,
    "--card": theme.card.bg,
    "--text": theme.color.text,
    "--muted": theme.color.textMuted,
    "--border": theme.color.border,
    "--thead": mix(theme.color.surfaceAlt, theme.color.background, 0.35),
    "--row-alt": mix(theme.color.surfaceAlt, theme.color.background, 0.55),
    "--bubble-user": theme.chat.userBubbleBg,
    "--bubble-assistant": theme.chat.aiBubbleBg,
    "--ui-bg": theme.color.background,
    "--ui-surface": theme.color.surface,
    "--ui-surface-2": theme.color.surfaceAlt,
    "--ui-text": theme.color.text,
    "--ui-muted": theme.color.textMuted,
    "--ui-border": theme.color.border,
    "--ui-accent": uiAccent,
    "--ui-accent-2": uiAccent2,
    "--ui-accent-3": uiAccent3,
    "--ui-accent-4": uiAccent4,
    "--ui-accent-5": uiAccent5,
    "--ui-shadow": theme.shadow.md,
    "--color-on-primary": theme.button.primary.fg,
    "--color-on-accent": pickReadableText(theme.color.accent),
    "--color-shadow": theme.shadow.md,
    "--color-overlay": theme.mode === "dark" ? "rgba(0,0,0,0.55)" : "rgba(15,23,42,0.35)",
    "--bg-gradient": gradient,
    "--bg-grid-opacity": theme.mode === "dark" ? "0.16" : "0.12",
    "--bg-photo-opacity": theme.mode === "dark" ? "0.28" : "0.2",
    "--glow-accent": glowAccent,
  };
}
